import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import batchLoadProducts from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import cleanupUploadData from "@insite/client-framework/Store/Components/OrderUpload/Handlers/CleanupUploadData";
import setErrorsModalIsOpen from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetErrorsModalIsOpen";
import setFailedToLoadInventory from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetFailedToLoadInventory";
import setIsBadFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsBadFile";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import addMultipleProducts from "@waytek/Store/Pages/QuickOrder/Handlers/AddMultipleProducts";
import { CustomState } from "@waytek/Store/Reducers";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomState) => ({
    uploadedItems: state.components.orderUpload.uploadedItems,
    products: state.components.orderUpload.products,
    rowErrors: state.components.orderUpload.rowErrors,
    isBadFile: state.components.orderUpload.isBadFile,
    productsProcessed: state.components.orderUpload.productsProcessed,
    continueProcess: state.components.orderUpload.continueUpload,
    errorsModalIsOpen: state.components.orderUpload.errorsModalIsOpen,
    productCopyPaste: state.productInformationState.productCopyPaste,
});

const mapDispatchToProps = {
    batchLoadProducts,
    cleanupUploadData,
    setIsBadFile,
    setErrorsModalIsOpen,
    addMultipleProducts,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderUploadStyles {
    modal?: ModalPresentationProps;
    errorText?: TypographyPresentationProps;
    itemWrapper?: InjectableCss;
    itemText?: TypographyPresentationProps;
}

export const orderUploadStyles: OrderUploadStyles = {
    modal: {
        sizeVariant: "small",
        cssOverrides: {
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
            `,
        },
    },
};

const styles = orderUploadStyles;

const QuickOrderCopyAndPasteProcess: FC<Props> = ({
    uploadedItems,
    products,
    rowErrors,
    isBadFile,
    productsProcessed,
    continueProcess,
    errorsModalIsOpen,
    batchLoadProducts,
    cleanupUploadData,
    setIsBadFile,
    setErrorsModalIsOpen,
    addMultipleProducts,
    productCopyPaste,
}) => {
    const toasterContext = useContext(ToasterContext);
    const [canceled, setCanceled] = useState(false);

    React.useEffect(() => {
        if (productCopyPaste) {
            batchGetProducts();
        }
    }, [uploadedItems]);

    React.useEffect(() => checkCompletion(), [productsProcessed]);

    const batchGetProducts = () => {
        cleanupUploadData();

        if (uploadedItems.length === 0) {
            return;
        }

        const extendedNames = uploadedItems.map(item => item.name);
        batchLoadProducts({ extendedNames, firstRowHeading: false, checkInventory: false });
    };

    const checkCompletion = React.useCallback(() => {
        if (!productsProcessed) {
            return;
        }

        if (uploadedItems.length === products.length && rowErrors.length === 0) {
            loadProducts();
        } else {
            setErrorsModalIsOpen({ errorsModalIsOpen: true });
        }
    }, [productsProcessed, uploadedItems, products, rowErrors]);

    const loadProducts = () => {
        addMultipleProducts({
            products,
            uploadedItems,
            onSuccess: () => {
                cleanupUploadData();
                // removing add to order toaster message for now as we are adding the products directly to the cart
                /* toasterContext.addToast({
                    body: `${products.length} ${translate("items added to order")}`,
                    messageType: "success",
                }); */
            },
        });
    };

    const closeModal = () => {
        setErrorsModalIsOpen({ errorsModalIsOpen: false });
    };

    const modalOnAfterCloseHandler = () => {
        setIsBadFile({ isBadFile: false });
        setFailedToLoadInventory({ failedToLoadInventory: false });
        if (canceled) {
            cleanupUploadData();
            setCanceled(false);
        }
    };

    React.useEffect(() => {
        if (continueProcess) {
            loadProducts();
        }
    }, [continueProcess]);

    if (!errorsModalIsOpen) {
        return null;
    }

    return (
        <Modal
            {...styles.modal}
            headline={siteMessage("QuickOrder_CopyPaste_ModalTitle")}
            isOpen={errorsModalIsOpen}
            handleClose={closeModal}
            onAfterClose={modalOnAfterCloseHandler}
            data-test-selector="quickOrder_CopyPasteError"
        >
            <Typography as="p" {...styles.errorText} data-test-selector="quickOrder_CopyPasteError_Description">
                {siteMessage("QuickOrder_CopyPaste_ErrorDescription")}
            </Typography>

            {rowErrors.map(({ index }) => (
                <StyledWrapper key={index} {...styles.itemWrapper}>
                    <Typography {...styles.itemText}>{translate("Line {0}", `${index}`)}</Typography>
                </StyledWrapper>
            ))}
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(QuickOrderCopyAndPasteProcess as any);
